"use client"

import React from "react"

import { forwardRef } from "react"
import type { IconType } from "react-icons"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  variant?: "primary" | "secondary" | "danger" | "success" | "outline" | "ghost" | "link"
  size?: "xs" | "sm" | "md" | "lg" | "xl"
  icon?: IconType | React.ReactNode
  iconPosition?: "left" | "right"
  fullWidth?: boolean
  isLoading?: boolean
  loadingText?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      type = "button",
      variant = "primary",
      size = "md",
      icon,
      iconPosition = "left",
      disabled = false,
      className = "",
      fullWidth = false,
      isLoading = false,
      loadingText,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"

    const variantStyles = {
      primary: "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 rounded-md",
      secondary: "bg-gray-600 text-white hover:bg-gray-700 focus-visible:ring-gray-500 rounded-md",
      danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 rounded-md",
      success: "bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500 rounded-md",
      outline: "border border-gray-300 bg-transparent hover:bg-gray-50 text-gray-700 rounded-md",
      ghost: "bg-transparent hover:bg-gray-100 text-gray-700",
      link: "bg-transparent text-blue-600 hover:text-blue-800 hover:underline p-0 h-auto",
    }

    const sizeStyles = {
      xs: "text-xs px-2 py-1 rounded-md",
      sm: "text-sm px-3 py-1.5 rounded-md",
      md: "text-sm px-4 py-2 rounded-md",
      lg: "text-base px-5 py-2.5 rounded-md",
      xl: "text-lg px-6 py-3 rounded-md",
    }

    const widthClass = fullWidth ? "w-full" : ""

    // Handle icon rendering
    const renderIcon = () => {
      if (!icon) return null

      // If icon is a React element
      if (React.isValidElement(icon)) {
        return React.cloneElement(icon as React.ReactElement, {
          className: `${children ? (iconPosition === "left" ? "mr-2" : "ml-2") : ""} h-4 w-4`,
        })
      }

      // If icon is an icon type
      const IconComponent = icon as IconType
      return <IconComponent className={`${children ? (iconPosition === "left" ? "mr-2" : "ml-2") : ""} h-4 w-4`} />
    }

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthClass} ${className}`}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className={`animate-spin -ml-1 mr-2 h-4 w-4 ${variant === "outline" || variant === "ghost" || variant === "link" ? "text-gray-700" : "text-white"}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {loadingText || children}
          </>
        ) : (
          <>
            {iconPosition === "left" && renderIcon()}
            {children}
            {iconPosition === "right" && renderIcon()}
          </>
        )}
      </button>
    )
  },
)

Button.displayName = "Button"

export default Button

